import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articlesAPI, commentsAPI } from '../utils/api';
import { DealBox } from '../components/DealBox';
import { CouponBox } from '../components/CouponBox';
import { Newsletter } from '../components/Newsletter';
import { SponsoredLabel } from '../components/SponsoredLabel';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SEO } from '../components/SEO';
import { ArticleContent } from '../components/ArticleContent';
import { RelatedArticles } from '../components/RelatedArticles';
import { FAQSchema } from '../components/FAQSchemaBlock';
import { InlineTableOfContents } from '../components/TableOfContents';
import { useRegion } from '../context/RegionContext';
import { Calendar, Clock, Eye, Share2, User, Twitter, Facebook, Linkedin, Link2, ArrowUp, BookOpen, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getArticleImage, handleImageError, FALLBACK_IMAGE } from '../utils/imageUtils';

export const ArticlePage = () => {
  const { slug } = useParams();
  const { currentRegion, region } = useRegion();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({ author_name: '', author_email: '', content: '' });
  const [loading, setLoading] = useState(true);
  const [readProgress, setReadProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [imageError, setImageError] = useState(false);
  const articleRef = useRef(null);

  // Build region-aware URLs
  const buildUrl = (path) => {
    if (currentRegion === 'global') return path;
    return `/${currentRegion}${path}`;
  };

  // Track reading progress
  useEffect(() => {
    const handleScroll = () => {
      if (!articleRef.current) return;
      
      const element = articleRef.current;
      const totalHeight = element.offsetHeight - window.innerHeight;
      const progress = Math.min(100, Math.max(0, (window.scrollY / totalHeight) * 100));
      setReadProgress(progress);
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });

    const fetchArticle = async () => {
      try {
        const response = await articlesAPI.getBySlug(slug);
        setArticle(response.data);
        
        const commentsRes = await commentsAPI.getByArticle(response.data.id);
        setComments(commentsRes.data);
      } catch (error) {
        console.error('Failed to fetch article:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    try {
      await commentsAPI.create({ ...commentForm, article_id: article.id });
      toast.success('Comment posted successfully!');
      setCommentForm({ author_name: '', author_email: '', content: '' });
      const commentsRes = await commentsAPI.getByArticle(article.id);
      setComments(commentsRes.data);
    } catch (error) {
      toast.error('Failed to post comment');
    }
  };

  const handleShare = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(article?.title || '');
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      copy: null
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-brand-red rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-inter">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <h1 className="text-5xl font-playfair font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Link 
            to={buildUrl('/')} 
            className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
          >
            Return to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Build breadcrumbs
  const breadcrumbItems = [
    { name: article.category_name, url: `/category/${article.category_id}` }
  ];

  // SEO breadcrumbs for schema
  const seoBreadcrumbs = [
    { name: 'Home', url: '/' },
    ...(currentRegion !== 'global' ? [{ name: region.name, url: `/${currentRegion}` }] : []),
    { name: article.category_name, url: `/category/${article.category_id}` },
    { name: article.title, url: `/article/${article.slug}` }
  ];

  const featuredImage = imageError ? FALLBACK_IMAGE : getArticleImage(article);

  return (
    <>
      <SEO 
        title={article.meta_title || article.title}
        description={article.meta_description || article.excerpt}
        type="article"
        article={article}
        breadcrumbs={seoBreadcrumbs}
        image={article.featured_image}
        canonicalPath={buildUrl(`/article/${article.slug}`)}
      />

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 z-50">
        <div 
          className="h-full bg-gradient-to-r from-brand-red to-red-500 transition-all duration-150"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      <div ref={articleRef} className="min-h-screen bg-white" data-testid="article-page">
        {/* Breadcrumbs */}
        <div className="bg-gray-50 border-b border-gray-100">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumbs items={breadcrumbItems} currentPage={article.title} />
          </div>
        </div>

        {/* Article Header */}
        <header className="bg-white pt-8 pb-12">
          <div className="max-w-[680px] mx-auto px-6">
            {/* Category & Sponsored Label */}
            <div className="flex items-center gap-3 mb-6">
              <Link
                to={buildUrl(`/category/${article.category_id}`)}
                className="inline-flex items-center gap-1 text-brand-red font-bold uppercase tracking-widest text-xs hover:underline"
                data-testid="article-category-link"
              >
                {article.category_name}
                <ChevronRight className="w-3 h-3" />
              </Link>
              {article.is_sponsored && <SponsoredLabel placement="inline" />}
            </div>

            {/* Title */}
            <h1 
              className="text-[28px] md:text-[42px] leading-[1.2] font-playfair font-bold text-gray-900 mb-6"
              style={{ letterSpacing: '-0.02em' }}
              data-testid="article-title"
            >
              {article.title}
            </h1>

            {/* Excerpt */}
            {article.excerpt && (
              <p className="text-base md:text-xl text-gray-600 leading-relaxed mb-8 font-inter">
                {article.excerpt}
              </p>
            )}

            {/* Author & Meta */}
            <div className="pb-8 border-b border-gray-200">
              {/* Author Row */}
              <div className="flex items-center gap-3 mb-4">
                {article.profile_author_avatar ? (
                  <img 
                    src={article.profile_author_avatar} 
                    alt={article.profile_author_name}
                    className="w-12 h-12 rounded-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-red to-red-600 flex items-center justify-center text-white font-bold text-lg">
                    {(article.profile_author_name || article.author_name)?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900" data-testid="article-author">
                    {article.profile_author_name || article.author_name}
                  </p>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span data-testid="article-date">
                      {format(new Date(article.published_at || article.created_at), 'MMMM dd, yyyy')}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1" data-testid="article-reading-time">
                      <BookOpen className="w-4 h-4" />
                      {article.reading_time} min read
                    </span>
                  </div>
                </div>
              </div>

              {/* Share Buttons - Left aligned below author */}
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleShare('twitter')}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-[#1DA1F2] hover:text-white flex items-center justify-center transition-colors"
                  title="Share on Twitter"
                  data-testid="share-twitter"
                >
                  <Twitter className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleShare('facebook')}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-[#1877F2] hover:text-white flex items-center justify-center transition-colors"
                  title="Share on Facebook"
                  data-testid="share-facebook"
                >
                  <Facebook className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleShare('linkedin')}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-[#0A66C2] hover:text-white flex items-center justify-center transition-colors"
                  title="Share on LinkedIn"
                  data-testid="share-linkedin"
                >
                  <Linkedin className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleShare('copy')}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-900 hover:text-white flex items-center justify-center transition-colors"
                  title="Copy link"
                  data-testid="share-copy"
                >
                  <Link2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {featuredImage && (
          <div className="max-w-[680px] mx-auto mb-12 px-6">
            <figure className="relative">
              <div className="aspect-[16/9] w-full overflow-hidden bg-gray-100">
                <img
                  src={featuredImage}
                  alt={article.featured_image_alt || article.title}
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={(e) => {
                    handleImageError(e);
                    setImageError(true);
                  }}
                  data-testid="article-featured-image"
                />
              </div>
              {article.featured_image_alt && (
                <figcaption className="text-sm text-gray-500 text-center mt-3 italic">
                  {article.featured_image_alt}
                </figcaption>
              )}
            </figure>
          </div>
        )}

        {/* Main Content */}
        <article className="max-w-[680px] mx-auto px-6 pb-16" data-testid="article-body-container">
          {/* Table of Contents - Auto-generated from H2/H3 */}
          {article.content && <InlineTableOfContents content={article.content} />}
          
          {/* Divider between TOC and Content */}
          <hr className="my-6 border-0 border-t border-gray-200" />
          
          {/* Article Content */}
          <div className="article-body">
            <ArticleContent content={article.content} />
          </div>

          {/* Inline CTA */}
          {article.deal_boxes && article.deal_boxes.length > 0 && (
            <div className="my-12 p-6 bg-gradient-to-r from-brand-red to-red-600 rounded-2xl text-white text-center">
              <p className="text-lg font-semibold mb-3">🔥 Found this helpful?</p>
              <a 
                href="#deals-section"
                className="inline-flex items-center gap-2 bg-white text-brand-red px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors"
              >
                Check Best Deals Below
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          )}

          {/* Author Bio Box */}
          {article.profile_author_id && (
            <div className="my-12 bg-gray-50 rounded-2xl p-8" data-testid="author-bio-box">
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-6 font-semibold border-b border-gray-200 pb-4">About the Author</p>
              <div className="flex items-center gap-4 mb-4">
                {article.profile_author_avatar ? (
                  <img 
                    src={article.profile_author_avatar} 
                    alt={article.profile_author_name}
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-white shadow-md flex-shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-red to-red-600 flex items-center justify-center text-white text-2xl font-bold ring-2 ring-white shadow-md flex-shrink-0">
                    {article.profile_author_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900">
                  {article.profile_author_name}
                </h3>
              </div>
              {article.profile_author_bio && (
                <p className="text-gray-600 leading-relaxed text-base mt-2">{article.profile_author_bio}</p>
              )}
            </div>
          )}

          {/* Deal Boxes */}
          {article.deal_boxes && article.deal_boxes.length > 0 && (
            <div id="deals-section" className="my-12 scroll-mt-8" data-testid="deal-boxes-section">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                🛒 Featured Deals
              </h2>
              <div className="space-y-4">
                {article.deal_boxes.map((deal, index) => (
                  <DealBox key={index} deal={deal} />
                ))}
              </div>
            </div>
          )}

          {/* Coupon Boxes */}
          {article.coupon_boxes && article.coupon_boxes.length > 0 && (
            <div className="my-12" data-testid="coupon-boxes-section">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                🏷️ Coupon Codes
              </h2>
              <div className="space-y-4">
                {article.coupon_boxes.map((coupon, index) => (
                  <CouponBox key={index} coupon={coupon} />
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="my-12" data-testid="article-tags">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Related Topics</h3>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag, index) => (
                  <Link
                    key={index}
                    to={buildUrl(`/search?q=${encodeURIComponent(tag)}`)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-brand-red hover:text-white rounded-full transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Schema Section */}
          {article.faqs && article.faqs.length > 0 && (
            <div className="my-12" data-testid="article-faqs">
              <FAQSchema faqs={article.faqs} />
            </div>
          )}
        </article>

        {/* Related Articles */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <RelatedArticles currentArticle={article} limit={6} />
          </div>
        </div>

        {/* Comments Section */}
        <div className="max-w-[720px] mx-auto px-6 py-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8" data-testid="comments-heading">
            Comments ({comments.length})
          </h2>
          
          <form onSubmit={handleCommentSubmit} className="mb-12 bg-gray-50 p-6 rounded-2xl" data-testid="comment-form">
            <h3 className="font-bold text-lg mb-4">Leave a Comment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                placeholder="Your Name"
                value={commentForm.author_name}
                onChange={(e) => setCommentForm({ ...commentForm, author_name: e.target.value })}
                required
                className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent bg-white"
                data-testid="comment-name-input"
              />
              <input
                type="email"
                placeholder="Your Email"
                value={commentForm.author_email}
                onChange={(e) => setCommentForm({ ...commentForm, author_email: e.target.value })}
                required
                className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent bg-white"
                data-testid="comment-email-input"
              />
            </div>
            <textarea
              placeholder="Share your thoughts..."
              value={commentForm.content}
              onChange={(e) => setCommentForm({ ...commentForm, content: e.target.value })}
              required
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent bg-white mb-4 resize-none"
              data-testid="comment-content-input"
            />
            <button
              type="submit"
              className="bg-brand-red text-white px-8 py-3 font-semibold hover:bg-red-700 transition-colors rounded-lg"
              data-testid="comment-submit-button"
            >
              Post Comment
            </button>
          </form>

          <div className="space-y-6" data-testid="comments-list">
            {comments.map((comment) => (
              <div key={comment.id} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-testid={`comment-${comment.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-gray-900">{comment.author_name}</span>
                  <span className="text-sm text-gray-500">
                    {format(new Date(comment.created_at), 'MMM dd, yyyy')}
                  </span>
                </div>
                <p className="text-gray-700 leading-relaxed">{comment.content}</p>
              </div>
            ))}
            
            {comments.length === 0 && (
              <p className="text-gray-500 text-center py-12 bg-gray-50 rounded-2xl">
                No comments yet. Be the first to share your thoughts!
              </p>
            )}
          </div>
        </div>

        {/* Newsletter */}
        <Newsletter />

        {/* Back to Top Button */}
        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 w-12 h-12 bg-brand-red text-white rounded-full shadow-lg hover:bg-red-700 transition-all hover:scale-110 flex items-center justify-center z-40"
            title="Back to top"
            data-testid="back-to-top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        )}
      </div>
    </>
  );
};

export default ArticlePage;
